import React from 'react';
import { View, Text, ViewStyle, TextStyle } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive, getResponsiveValue } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  backButton?: React.ReactNode;
  style?: ViewStyle;
}

/**
 * Reusable page header component with consistent styling
 * Ensures all page headers look professional and uniform
 */
export function PageHeader({
  title,
  subtitle,
  action,
  backButton,
  style,
}: PageHeaderProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile, isSmallMobile } = useResponsive();

  // Responsive typography - smaller on mobile for better fit
  const headingSize = getResponsiveValue(
    isSmallMobile ? 22 : 24,  // small mobile / mobile
    28,                        // tablet
    32,                        // desktop
    32                         // large desktop
  );
  const bodySize = getResponsiveValue(13, 14, 15, 16);

  return (
    <View
      style={[
        {
          marginBottom: isMobile ? utils.spacing.lg : utils.spacing['2xl'],
        },
        style,
      ]}
    >
      {backButton && (
        <View style={{ marginBottom: utils.spacing.md }}>
          {backButton}
        </View>
      )}
      
      <View
        style={{
          flexDirection: isMobile && action ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile && action ? 'stretch' : 'flex-start',
          gap: isMobile ? utils.spacing.sm : utils.spacing.md,
        }}
      >
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={[
              {
                fontSize: headingSize,
                fontWeight: utils.typography.weights.bold,
                marginBottom: subtitle ? utils.spacing.xs : 0,
                letterSpacing: -0.5,
                color: tokens.color.onBackground,
                fontFamily: utils.typography.heading.fontFamily,
                lineHeight: headingSize * 1.2,
                width: '100%',
              },
            ]}
            numberOfLines={isMobile ? 2 : undefined}
          >
            {title}
          </Text>
          {subtitle && (
            <Text
              style={[
                {
                  fontSize: bodySize,
                  color: addColorOpacity(tokens.color.onBackground, 0.7),
                  fontFamily: utils.typography.body.fontFamily,
                  lineHeight: bodySize * 1.5,
                  width: '100%',
                },
              ]}
              numberOfLines={isMobile ? 2 : undefined}
            >
              {subtitle}
            </Text>
          )}
        </View>
        
        {action && (
          <View style={{ 
            marginLeft: isMobile ? 0 : utils.spacing.md,
            marginTop: isMobile ? utils.spacing.sm : 0,
            flexShrink: 0,
          }}>
            {action}
          </View>
        )}
      </View>
    </View>
  );
}

